"use client";

// ============================================================
// CLUBHUB – Eine Spende als Blatt
// ============================================================
// Eine Zeile der Liste oeffnet dieses Blatt (`?spende=<id>` in der
// Adresse — der Filter dahinter bleibt stehen, „Zurück“ schliesst
// es). Es zeigt alles, was zur Spende gehoert, und fuehrt weiter:
// zum Spender, zum Aufruf, zur Bestätigung.
// ============================================================

import { useState, useTransition } from "react";
import Link from "next/link";
import { ArrowUpRight, FileCheck2, Lock, Pencil, Trash2 } from "lucide-react";
import { Button, confirmDialog, toast } from "@/components/ui";
import { Seitenblatt } from "@/components/ui/sheet";
import { useAnsicht } from "@/components/liste";
import { DONATION_KIND, PAYMENT_METHOD } from "@/lib/labels";
import { formatDate, formatDateTime } from "@/lib/utils";
import { kontoauszugGenuegt } from "@/lib/spenden/auswertung";
import { ANSPRUCH_GRUNDLAGE, HERKUNFT, WERTERMITTLUNG, euro, jahrVon } from "@/lib/spenden/format";
import type { SpendeView } from "@/lib/spenden/views";
import { spendeLoeschen } from "@/app/(app)/spenden/actions";
import { Angabe, ArtBadge, SpenderZelle } from "./kleinteile";
import { SpendeDialog, type AufrufWahl } from "./spende-dialog";

export function SpendeBlatt({
  spende,
  aufrufe,
  darfSchreiben,
  vereinfachtBis,
  ohneBestaetigung = false,
}: {
  spende: SpendeView | null;
  aufrufe: AufrufWahl[];
  darfSchreiben: boolean;
  vereinfachtBis: number;
  /** Der Verein darf keine Bestätigungen ausstellen (nicht gemeinnützig). */
  ohneBestaetigung?: boolean;
}) {
  const { setzen } = useAnsicht();
  const [bearbeiten, setBearbeiten] = useState(false);
  const [loeschtGerade, starte] = useTransition();
  const schliessen = () => setzen({ spende: null });

  async function loeschen() {
    if (!spende) return;
    const ja = await confirmDialog({
      title: "Spende löschen?",
      description: `${euro(spende.amount)} vom ${formatDate(spende.date)} verschwindet aus Liste, Aufruf und Summen. Das lässt sich nicht rückgängig machen.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ja) return;
    starte(async () => {
      const r = await spendeLoeschen(spende.id);
      if ("error" in r && r.error) toast.error(r.error);
      else {
        toast.success("Spende gelöscht");
        schliessen();
      }
    });
  }

  return (
    <>
      <Seitenblatt
        offen={Boolean(spende) && !bearbeiten}
        onSchliessen={schliessen}
        titel={spende ? `Spende vom ${formatDate(spende.date)}` : "Spende"}
        kopfZusatz={
          spende && (
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <ArtBadge kind={spende.kind} />
                <span className="text-caption text-text-tertiary tabular-nums">{formatDate(spende.date)}</span>
              </div>
              <p className="text-heading-lg text-text-primary mt-1 tabular-nums">{euro(spende.amount)}</p>
              <p className="text-body-sm mt-0.5">
                <SpenderZelle spende={spende} />
              </p>
            </div>
          )
        }
      >
        {spende && (
          <div className="space-y-6">
            {darfSchreiben && (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setBearbeiten(true)}>
                  <Pencil className="h-3.5 w-3.5" aria-hidden />
                  Bearbeiten
                </Button>
                {!spende.receiptId && (
                  <Button variant="ghost" size="sm" className="text-danger hover:text-danger" onClick={loeschen} loading={loeschtGerade}>
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    Löschen
                  </Button>
                )}
              </div>
            )}

            <dl className="divide-border-subtle divide-y">
              <Angabe label="Art">{DONATION_KIND[spende.kind]}</Angabe>
              <Angabe label="Spender">
                {spende.contactId ? (
                  <span className="flex flex-col gap-0.5">
                    <Link href={`/spenden/spender/${spende.contactId}`} className="hover:text-accent inline-flex items-center gap-1 font-medium">
                      {spende.hinterlegterName}
                      <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                    </Link>
                    {spende.isAnonymous && (
                      <span className="text-caption text-text-secondary inline-flex items-center gap-1">
                        <Lock className="h-3 w-3" aria-hidden />
                        Wünscht keine Nennung — erscheint überall als „Anonym“, bekommt aber die Bestätigung.
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-text-secondary">Ohne Namen eingegangen</span>
                )}
              </Angabe>
              {spende.contactId && (
                <Angabe label="Auf der Website">
                  {spende.namentlichOeffentlich ? "Mit Namen (Einwilligung liegt vor)" : spende.isAnonymous ? "Als „Anonym“" : "Ohne Namen — keine Einwilligung „Internet“"}
                </Angabe>
              )}
              {spende.kind === "MONEY" && <Angabe label="Zahlungsart">{PAYMENT_METHOD[spende.method]}</Angabe>}
              <Angabe label="Aufruf">
                {spende.campaignId ? (
                  <Link href={`/spenden/aufrufe/${spende.campaignId}`} className="hover:text-accent inline-flex items-center gap-1">
                    {spende.campaignTitle}
                    <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                  </Link>
                ) : (
                  <span className="text-text-tertiary">–</span>
                )}
              </Angabe>
              <Angabe label="Zweck">{spende.purpose ?? <span className="text-text-tertiary">–</span>}</Angabe>
              {spende.note && <Angabe label="Notiz">{spende.note}</Angabe>}
            </dl>

            {spende.sach && (
              <section>
                <h3 className="text-overline text-text-tertiary mb-1">Sachzuwendung</h3>
                <dl className="divide-border-subtle divide-y">
                  <Angabe label="Bezeichnung">{spende.sach.bezeichnung}</Angabe>
                  <Angabe label="Alter, Zustand">{spende.sach.alterZustand}</Angabe>
                  <Angabe label="Wertermittlung">
                    {WERTERMITTLUNG[spende.sach.wertermittlung]}
                    {spende.sach.wertGrundlage && <span className="text-caption text-text-tertiary block">{spende.sach.wertGrundlage}</span>}
                  </Angabe>
                  <Angabe label="Herkunft">{HERKUNFT[spende.sach.herkunft].label}</Angabe>
                  <Angabe label="Unterlagen">
                    {spende.sach.unterlagenLiegenVor ? (
                      "Liegen vor"
                    ) : (
                      <span className="text-warning">Fehlen noch — vor der Bestätigung nachreichen lassen</span>
                    )}
                  </Angabe>
                </dl>
              </section>
            )}

            {spende.verzicht && (
              <section>
                <h3 className="text-overline text-text-tertiary mb-1">Verzicht auf Erstattung</h3>
                <dl className="divide-border-subtle divide-y">
                  <Angabe label="Anspruch">{spende.verzicht.anspruch}</Angabe>
                  <Angabe label="Grundlage">
                    {ANSPRUCH_GRUNDLAGE[spende.verzicht.grundlage]}
                    {spende.verzicht.grundlageVom && <span className="tabular-nums"> vom {formatDate(spende.verzicht.grundlageVom)}</span>}
                  </Angabe>
                  <Angabe label="Verzicht erklärt">
                    <span className="tabular-nums">{formatDate(spende.verzicht.verzichtVom)}</span>
                  </Angabe>
                </dl>
              </section>
            )}

            <section className="border-border bg-bg-primary rounded-xl border p-4">
              <div className="flex items-start gap-3">
                <FileCheck2 className={spende.receiptId ? "text-success mt-0.5 h-4 w-4 shrink-0" : "text-text-tertiary mt-0.5 h-4 w-4 shrink-0"} aria-hidden />
                <div className="min-w-0 flex-1">
                  {spende.receiptId ? (
                    <>
                      <p className="text-body-sm text-text-primary font-medium">Bestätigt</p>
                      <Link href={`/spenden/bestaetigungen/${spende.receiptId}`} className="text-body-sm text-accent hover:underline">
                        Zuwendungsbestätigung {spende.receiptNumber}
                      </Link>
                    </>
                  ) : ohneBestaetigung ? (
                    <>
                      <p className="text-body-sm text-text-primary font-medium">Keine Zuwendungsbestätigung</p>
                      <p className="text-caption text-text-secondary mt-0.5">
                        Der Verein ist nicht als gemeinnützig eingetragen und darf deshalb keine Zuwendungsbestätigungen ausstellen. Die Spende ist für die Buchhaltung erfasst.
                      </p>
                    </>
                  ) : spende.contactId ? (
                    <>
                      <p className="text-body-sm text-text-primary font-medium">
                        {kontoauszugGenuegt(spende, vereinfachtBis) ? "Noch keine Bestätigung — der Kontoauszug genügt" : "Zuwendungsbestätigung nötig"}
                      </p>
                      <p className="text-caption text-text-secondary mt-0.5">
                        {kontoauszugGenuegt(spende, vereinfachtBis)
                          ? `Bis ${euro(vereinfachtBis)} je Zuwendung erkennt das Finanzamt den Kontoauszug als Nachweis an (§ 50 Abs. 4 EStDV). Eine Bestätigung bleibt möglich. `
                          : spende.kind !== "MONEY" || spende.method === "CASH"
                            ? `${spende.kind === "IN_KIND" ? "Eine Sachspende" : spende.kind === "WAIVER" ? "Ein Aufwandsverzicht" : "Eine Barspende"} braucht immer eine Bestätigung — einen Kontoauszug gibt es dafür nicht. `
                            : `Über ${euro(vereinfachtBis)} je Zuwendung braucht das Finanzamt eine Zuwendungsbestätigung. `}
                        Mehrere Geldspenden eines Jahres lassen sich zu einer Sammelbestätigung bündeln.
                      </p>
                      {darfSchreiben && (
                        <Link
                          href={`/spenden/bestaetigungen/erstellen?jahr=${jahrVon(spende.date)}&person=${spende.contactId}`}
                          className="text-body-sm text-accent mt-2 inline-block hover:underline"
                        >
                          Bestätigung erstellen
                        </Link>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-body-sm text-text-primary font-medium">Keine Bestätigung möglich</p>
                      <p className="text-caption text-text-secondary mt-0.5">Ohne Name und Anschrift lässt sich nichts bestätigen.</p>
                    </>
                  )}
                </div>
              </div>
            </section>

            <p className="text-caption text-text-tertiary">Erfasst am {formatDateTime(spende.createdAt)}</p>
          </div>
        )}
      </Seitenblatt>

      {spende && bearbeiten && <SpendeDialog open onClose={() => setBearbeiten(false)} spende={spende} aufrufe={aufrufe} />}
    </>
  );
}
